import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { SelectionStateService } from '../selection-state.service';
import { EventAccessService } from '../event-access/event-access.service';
import { RoleNavigationService } from '../role-navigation.service';
import { ToastService } from '../toast.service';
import { isReportRole } from 'src/app/types/user-role.types';

const FORBIDDEN_TOAST_DURATION_MS = 5000;
const FORBIDDEN_TOAST_COLOR = 'warning';

function isForbidden(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === HttpStatusCode.Forbidden;
}

/**
 * 403 en un reporte del evento con un rol de reporte: el permiso se revocó mientras el usuario
 * estaba adentro. Se avisa, se invalida la lista de eventos (para que el evento/rol desaparezca)
 * y se vuelve a la lista con el rol `seller`. Los requests en paralelo que también den 403 no
 * repiten el aviso ni la navegación.
 */
@Injectable({ providedIn: 'root' })
export class ReportForbiddenHandler {
  private handling = false;

  constructor(
    private readonly selectionState: SelectionStateService,
    private readonly eventAccess: EventAccessService,
    private readonly roleNavigation: RoleNavigationService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly logger: NGXLogger,
  ) {}

  /** Devuelve `true` si el error es un 403 de reporte (manejado o en curso de manejarse). */
  handle(error: unknown): boolean {
    if (!isForbidden(error)) {
      return false;
    }
    // El primero ya pasó el rol a `seller`: los 403 en paralelo del mismo reporte se absorben.
    if (this.handling) {
      return true;
    }
    if (!isReportRole(this.selectionState.selectedRole())) {
      return false;
    }
    this.handling = true;
    this.leaveEvent().finally(() => (this.handling = false));
    return true;
  }

  private async leaveEvent(): Promise<void> {
    try {
      this.eventAccess.invalidate();
      await this.selectionState.setGlobalContext();
      this.roleNavigation.navigateToEventList();
      await this.toast.showToast(
        this.translate.instant('event-access.forbidden'),
        FORBIDDEN_TOAST_DURATION_MS,
        FORBIDDEN_TOAST_COLOR,
      );
    } catch (error: unknown) {
      this.logger.error('No se pudo salir del evento tras un 403 de reporte', { error });
    }
  }
}
